import React, { useMemo } from "react";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Tooltip } from "recharts";
import { xFactorOf, X_CEILING, salesCounted } from "@/lib/customRatios";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
const big = (v, cur) => {
    if (v == null) return "—";
    const a = Math.abs(v); const s = v < 0 ? "−" : "";
    const u = cur ? ` ${cur}` : "";
    if (a >= 1e9) return `${s}${(a / 1e9).toLocaleString("es-ES", { maximumFractionDigits: 1 })} mil M${u}`;
    if (a >= 1e6) return `${s}${(a / 1e6).toLocaleString("es-ES", { maximumFractionDigits: 0 })} M${u}`;
    return `${s}${a.toLocaleString("es-ES", { maximumFractionDigits: 2 })}${u}`;
};
const num = (v, d = 2) => (v == null ? "—" : v.toLocaleString("es-ES", { maximumFractionDigits: d }));
// Revenue +2y over market cap on a log scale: 3 % of the market cap → 0, as much as it → 100.
const SALES_LOW = 0.03;
const salesScore = (s) => (s == null || s <= 0 ? 0 : clamp(Math.log(s / SALES_LOW) / Math.log(1 / SALES_LOW) * 100, 0, 100));

const RadarTip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
        <div className="bg-white border border-black px-2 py-1 text-xs font-mono shadow-sm">
            <div className="font-semibold">{p.axis}</div>
            <div>{p.raw}{p.hint ? <span className="text-[#7A7A7A]"> · {p.hint}</span> : ""}</div>
        </div>
    );
};

// Two-line, vertically-centred axis labels so long ones (e.g. "Solidez (FCF − deuda)"
// on the left vertex) don't get clipped against the container edge.
const renderAxisTick = ({ payload, x, y, textAnchor }) => {
    const words = String(payload.value).split(" ");
    const lines = words.length > 1 ? [words[0], words.slice(1).join(" ")] : [String(payload.value)];
    const lineH = 10;
    const startDy = -((lines.length - 1) / 2) * lineH;
    return (
        <text x={x} y={y} textAnchor={textAnchor} fontFamily="IBM Plex Mono" fontSize={9.5} fill="#4A4A4A">
            {lines.map((ln, i) => (
                <tspan key={i} x={x} dy={i === 0 ? startDy : lineH}>{ln}</tspan>
            ))}
        </text>
    );
};

// Hexagon (radar) of the members of the POC/POV formula: revenue per share +2y,
// gross margin, x (FCF +2y − net debt over market cap), the two 4-year CAGRs and
// the operating margin (only in the POV). Each axis is scored 0-100 (higher =
// raises the POC more); raw values in the tooltip. Reads live `inputs` so it
// reflects the user's edited overrides.
export const MetricsRadar = ({ inputs, currency }) => {
    const data = useMemo(() => {
        if (!inputs) return [];
        const n = (v) => (v == null || v === "" || isNaN(Number(v)) ? null : Number(v));
        const gm = n(inputs.gross_margin), om = n(inputs.operating_margin);
        const rc = n(inputs.revenue_cagr_4y), fc = n(inputs.fcf_cagr_4y);
        const nd = n(inputs.net_debt), mc = n(inputs.market_cap), fcf = n(inputs.fcf_2y);
        const rev = n(inputs.revenue_2y), sh = n(inputs.shares_outstanding);
        const sales = rev != null && mc ? rev / mc : null;
        // What the formula counts (revenue above the market cap with a thin margin counts less).
        const counted = sales == null ? null : salesCounted(sales, om ?? 0);
        const rps = rev != null && sh ? rev / sh : null;
        const xRaw = fcf != null && nd != null && mc ? (fcf - nd) / mc * 100 : null;
        const xf = xRaw == null ? null : xFactorOf(xRaw);
        return [
            { axis: "Ingresos por acción", score: salesScore(counted), raw: `${num(rps)}${currency ? ` ${currency}` : ""} (+2 años)`,
              hint: sales == null ? "" : `${num(sales)} veces la capitalización${counted < sales - 0.005 ? ` (cuentan ${num(counted)} por su margen bajo)` : ""} · escala 3 %→100 % de ella` },
            { axis: "Margen bruto", score: gm == null ? 0 : clamp(gm * 100, 0, 100), raw: pct(gm), hint: "escala 0→100%" },
            { axis: "Solidez (FCF − deuda)", score: xf == null ? 0 : clamp((xf - 0.5) / (X_CEILING - 0.5) * 100, 0, 100),
              raw: xRaw == null ? "—" : `x ${num(xRaw, 1)} % → factor ${num(xf)}`,
              hint: `(FCF +2a ${big(fcf, currency)} − deuda neta ${big(nd, currency)}) ÷ capitalización · factor 0,5→6` },
            { axis: "CAGR ingresos", score: rc == null ? 0 : clamp(rc / 0.30 * 100, 0, 100), raw: pct(rc), hint: "escala 0→30%" },
            { axis: "CAGR FCF", score: fc == null ? 0 : clamp(fc / 0.30 * 100, 0, 100), raw: pct(fc), hint: "escala 0→30%" },
            { axis: "Margen operativo", score: om == null ? 0 : clamp(om / 0.40 * 100, 0, 100), raw: pct(om), hint: "solo en el POV · escala 0→40%" },
        ];
    }, [inputs, currency]);

    if (!data.length) return null;

    return (
        <div className="w-full h-full flex flex-col" data-testid="metrics-radar">
            <div className="overline text-[#4A4A4A]">Perfil financiero</div>
            <div className="flex-1 min-h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={data} outerRadius="65%" margin={{ top: 14, right: 34, bottom: 10, left: 34 }}>
                        <PolarGrid stroke="#00000020" />
                        <PolarAngleAxis dataKey="axis" tick={renderAxisTick} />
                        <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                        <Radar dataKey="score" stroke="var(--chart-blue)" fill="var(--chart-blue)" fillOpacity={0.35} strokeWidth={1.5} isAnimationActive={false} />
                        <Tooltip content={<RadarTip />} />
                    </RadarChart>
                </ResponsiveContainer>
            </div>
            <div className="text-[10px] text-[#7A7A7A] italic mt-1">Los seis miembros de la fórmula, cada uno de 0 a 100 (mayor = sube más el POC). Pasa el cursor para ver el valor real.</div>
        </div>
    );
};

export default MetricsRadar;
